import React, { useState, useEffect, useRef, useMemo } from 'react';
import theme, { themeColors } from 'themes';
import styled from 'styled-components';
import { DropDownItem } from 'pages/ProteinMachine/MachineConfig/Common/Alerts/FormElementsTypes';
import { RadioButton } from 'components';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import ChevronUp from 'icons/indexed-icons/ChevronUp';

interface Props {
  userId?: string;
  groups?: DropDownItem[];
  handleOptionSelect?: (id: string, option?: OptionType, parentOption?: OptionType) => void;
}
interface containerProps {
  containerPosition?: string;
}
export interface OptionType {
  value: string;
  label?: string;
  isAdmin?: boolean;
  isActive?: boolean;
  isChecked?: boolean;
  showSubMenu?: boolean;
  subMenu?: OptionType[];
}

const IconContainer = styled.div`
  all: unset;
  cursor: pointer;
  line-height: 0;
  box-sizing: border-box;
  align-content: center;
  padding-right: 10px;
  svg {
    width: 11px;
  }
  :focus {
    background: ${theme.colors.lightGrey10};
    border-radius: 0.3rem;
    border: 1px solid ${theme.colors.primaryBlue5 + '80'};
    box-sizing: border-box;
  }
`;
const OptionText = styled.div`
  text-align: left;
  padding-top: 5px;
  padding-left: 4px;
  white-space: nowrap;
  width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;
const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 10px;
  margin-bottom: 16px;
`;
const TextContent = styled.div<{ width?: string }>`
  width: ${({ width }) => width || '0'};
  padding-top: 0.25rem;
  pointer-events: none;
  user-select: none;
  background-color: ${theme.colors.white};
  color: ${themeColors.textLightBlack};
  border-radius: 1.25rem;
  border: 0.063rem solid ${theme.colors.primaryBlue5};
  padding: 0.4rem;
  height: 30px;
  text-align: center;
`;
const TextPlaceHolder = styled.div<{ width?: string }>`
  width: ${({ width }) => width || '0'};
  padding-top: 0.25rem;
  pointer-events: none;
  user-select: none;
`;
export const OptionWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  > input {
    scale: 1.3 !important;
  }
  padding-left: 12px;
  margin-bottom: 14px;
  }
`;
export const LabelWrapper = styled.div`
  margin-left: -4rem;
  label {
    font-size: 0.875rem;
    font-weight: 400;
  }
`;
export const OptionItem = styled.div<{ width?: string; padding?: string }>`
  width: ${({ width }) => width};
  cursor: pointer;
  position: relative;
`;
const SubOptionItem = styled.li<{ width?: string; padding?: string }>`
  width: ${({ width }) => width};
  padding-bottom: 5px;
  cursor: pointer;
  position: relative;
  list-style: none;
`;
const SubmenuContainer = styled.ul<{ width?: string }>`
  width: ${({ width }) => width};
  padding: 0;
  margin-bottom: 0.625rem;
  background-color: white;
`;
const DropdownContainer = styled.div<containerProps>`
  position: relative;
  border-radius: 6px;
  padding-left: 8px;
  border: 0.063rem solid ${theme.colors.tableborder};
  cursor: pointer;
  &:hover {
    border-color: ${theme.colors.disabledColor};
  }
`;
const DropdownInput = styled.div`
  display: flex;
  gap: 0.313rem;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  color: #808080;
`;
const PopupContainer = styled.div<{ top?: string; overflow?: string; width?: string }>`
  position: fixed;
  width: ${({ width }) => width || 'max-content'};
  z-index: 9999;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.1),
    0 6px 20px 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  max-height: 20rem;
  overflow: auto;
`;
const DropDownMultiRadioSelect: React.FC<Props> = ({ userId, groups, handleOptionSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupCords, setPopupCords] = useState<{
    xPx: string;
    yPx: string;
    yOffSetPx: string;
    x: number;
    y: number;
    yOffSet: number;
    xOffSetPx: string;
    xOffSet: number;
  }>({
    x: 0,
    y: 0,
    yOffSet: 0,
    xPx: '',
    yPx: '',
    yOffSetPx: '',
    xOffSetPx: '',
    xOffSet: 0
  });

  useEffect(() => {
    const popup = document.getElementById('import-selected-popup');
    if (popup && popupCords.x && popupCords.y) {
      const viewPortOffset = window.innerHeight - (popupCords.y + popup.offsetHeight);
      if (popup.offsetHeight > viewPortOffset) {
        popup.style.top = (popupCords.y - popup.offsetHeight).toString().concat('px');
        popup.style.left = popupCords.xOffSetPx;
      } else {
        popup.style.top = popupCords.yOffSetPx;
        popup.style.left = popupCords.xOffSetPx;
      }
    }
  }, [popupCords]);
  const options = useMemo(() => {
    const opt = groups?.map((group) => ({
      label: group.label,
      value: group.value as string,
      isAdmin: group.isAdmin,
      subMenu: [
        { label: 'Admin', value: 'admin', isChecked: group.isAdmin },
        { label: 'Member', value: 'member', isChecked: !group.isAdmin }
      ]
    }));
    return opt;
  }, [groups]);

  const adminInit = useMemo(() => {
    if (!groups) return false;
    return groups.some((group) => group.isAdmin);
  }, [groups]);

  const memberInit = useMemo(() => {
    if (!groups) return false;
    return groups.some((group) => !group.isAdmin);
  }, [groups]);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  // Function to toggle the dropdown visibility
  const toggleDropdown = () => {
    const parent = dropdownRef?.current?.getBoundingClientRect();
    const x = parent?.x + parent?.width - 14;
    const y = parent?.y + parent?.height - 14;
    if (dropdownRef?.current) {
      setPopupCords((prev) => {
        return {
          ...prev,
          x: x,
          y: y,
          yOffSet: y + 15,
          xPx: x.toString().concat('px'),
          yPx: y.toString().concat('px'),
          yOffSetPx: (y + 15).toString().concat('px'),
          xOffSetPx: (x - (192 - 14)).toString().concat('px'),
          xOffSet: x - (192 - 14) // x - (y-z): x: point of mouse event
          // y: width of select box
          // z: width of chevroniconbox /2
          // ensures that the dropdown is positioned approx. adjacent to the parent
        };
      });
    }
    setIsOpen(!isOpen);
  };

  const handleOption = (option: OptionType, parentOption?: OptionType) => {
    handleOptionSelect?.(userId as string, option, parentOption);
  };

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer
      ref={dropdownRef}
      style={{ justifyContent: 'space-between', minWidth: '112px', left: '-3px' }}
    >
      <DropdownInput
        onClick={() => {
          groups && groups.length > 0 && toggleDropdown();
        }}
      >
        {adminInit && <TextContent width="4.4rem">Admin</TextContent>}
        {memberInit && <TextContent width="4.4rem">Member</TextContent>}
        {!adminInit && !memberInit && <TextPlaceHolder width="100%">Select</TextPlaceHolder>}
        <IconContainer>{isOpen ? <ChevronUp /> : <ChevronDown />}</IconContainer>
      </DropdownInput>
      {isOpen && (
        <PopupContainer id="import-selected-popup">
          {options?.map((option) => {
            const { subMenu } = option;
            return (
              <OptionItem width="12rem" key={option.value} padding=".8rem 1rem">
                <OptionRow>
                  <OptionText>{option?.label}</OptionText>
                </OptionRow>
                <SubmenuContainer width="12rem">
                  {subMenu?.map((menu) => (
                    <SubOptionItem
                      padding={menu.isChecked ? '.8rem 1rem' : '.8rem 2rem'}
                      width="9rem"
                    >
                      <OptionWrapper>
                        <RadioButton
                          label={menu.label}
                          labelWeight={400}
                          width={18}
                          height={18}
                          fontSize={14}
                          labelColor={theme.colors.text.lightBlack}
                          checked={menu.isChecked}
                          onChange={() => {
                            !menu.isChecked && handleOption(menu, option);
                          }}
                        />
                      </OptionWrapper>
                    </SubOptionItem>
                  ))}
                </SubmenuContainer>
              </OptionItem>
            );
          })}
        </PopupContainer>
      )}
    </DropdownContainer>
  );
};

export default DropDownMultiRadioSelect;
